import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import ExcludedTitlesNotice from "../../components/director/ExcludedTitlesNotice";
import CoverageBars from "../../components/director/module1/CoverageBars";
import MetricCard from "../../components/director/module1/MetricCard";
import ModuleShell from "../../components/director/module1/ModuleShell";
import ModuleState from "../../components/director/module1/ModuleState";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import { useDirectorContext } from "@contexts/director";
import {
  compactCents,
  formatBps,
  formatCompetenceShort,
  formatFreshness,
  sumKnownCents,
} from "../../utils/directorFormat";
import { companyQuery, currentCompetence, oldestExtractedAt } from "./module1Utils";
import useProjectRoute from "./useProjectRoute";

const BASE_PATH = "/dashboard/director/receivables-commitments";
const ALL_LABEL = "Tudo, com despesas da empresa";

const monthOrNone = (value, none = "Nenhum") => (value ? formatCompetenceShort(value) : none);

const commitmentsOf = (row = {}) => sumKnownCents(row.committedCents, row.plannedCents);

const hasPosition = (summary) => Number.isSafeInteger(summary?.openingPositionCents);

const Switch = ({ label, options, value, onChange }) => <div className="director-owner-switch" role="group" aria-label={label}>
  {options.map((option) => <button
    key={option.value}
    type="button"
    aria-pressed={value === option.value ? "true" : "false"}
    onClick={() => onChange(option.value)}
  >
    {option.label}
  </button>)}
</div>;

const FlowKpis = ({ summary, months, project }) => {
  const worst = summary.worstCumulativeCents;
  const hasGap = Number.isSafeInteger(worst) && worst < 0;
  return <MetricCard
    label={project ? "Falta acumulada" : "Maior descoberto acumulado"}
    value={hasGap ? compactCents(Math.abs(worst)) : "Sem descoberto"}
    note={hasGap
      ? `${project ? "Até" : "Em"} ${monthOrNone(summary.worstCumulativeMonth, "mês não informado")}, só com o fluxo destes ${months} meses`
      : `O fluxo destes ${months} meses não fica negativo`}
    tone={hasGap ? "pressure" : "neutral"}
  />;
};

const Kpis = ({ summary = {}, horizonMonths, project, reading, firstMonth }) => {
  const commitments = commitmentsOf(summary);
  const months = horizonMonths || 12;
  const byPosition = reading === "position";
  const lowest = summary.lowestPositionCents;
  return <section className="director-module1__metrics director-module1__metrics--four" aria-label="Indicadores de cobertura">
    {byPosition && <MetricCard
      label="Posição própria hoje"
      value={compactCents(summary.openingPositionCents)}
      note={firstMonth
        ? `Recebido − pago desde ${formatCompetenceShort(firstMonth)}`
        : "Sem movimento realizado antes deste mês"}
      tone={summary.openingPositionCents < 0 ? "pressure" : "neutral"}
    />}
    <MetricCard
      label={`Entradas previstas · ${months} meses`}
      value={compactCents(summary.receivableCents)}
      note="Parcelas da carteira e outras receitas do ERP"
      tone="blue"
    />
    <MetricCard
      label={`Compromissos · ${months} meses`}
      value={compactCents(commitments)}
      note={`Contratados ${compactCents(summary.committedCents)} · planejados ${compactCents(summary.plannedCents)}`}
      tone="orange"
    />
    {!byPosition && <MetricCard
      label="Cobertura"
      value={formatBps(summary.coverageBps)}
      note={project
        ? `${Number.isSafeInteger(summary.uncoveredMonths) ? summary.uncoveredMonths : 0} de ${months} meses descobertos`
        : "Entradas previstas ÷ compromissos"}
      tone={Number.isSafeInteger(summary.coverageBps) && summary.coverageBps < 10000 ? "pressure" : "green"}
    />}
    {byPosition
      ? <MetricCard
        label={`Menor posição · ${months} meses`}
        value={compactCents(lowest)}
        note={`Em ${monthOrNone(summary.lowestPositionMonth, "mês não informado")}`}
        tone={Number.isSafeInteger(lowest) && lowest < 0 ? "pressure" : "neutral"}
      />
      : <FlowKpis summary={summary} months={months} project={project} />}
  </section>;
};

const ProjectsTable = ({ data, selectedCompanyId, reading }) => {
  const projects = data.projects || [];
  const summary = data.summary || {};
  const byPosition = reading === "position";
  const endLabel = `Posição em ${monthOrNone(data.horizon?.to, "fim")}`;
  const row = (values) => <>
    <td>{compactCents(values.receivableCents)}</td>
    <td>{compactCents(commitmentsOf(values))}</td>
    {byPosition
      ? <>
        <td>{compactCents(values.openingPositionCents)}</td>
        <td>{compactCents(values.endPositionCents)}</td>
      </>
      : <>
        <td>{Number.isSafeInteger(values.coverageBps) ? formatBps(values.coverageBps) : "—"}</td>
        <td>{values.firstUncoveredMonth === undefined ? "—" : monthOrNone(values.firstUncoveredMonth)}</td>
      </>}
  </>;
  return <article className="director-module1__panel">
    <header>
      <div>
        <span className="director-eyebrow">Por obra</span>
        <h2>Quem se paga e quem precisa de aporte</h2>
      </div>
    </header>
    <div className="director-table-scroll">
      <table className="director-table director-owner-table" aria-label="Cobertura por obra">
        <thead>
          <tr>
            <th scope="col">Obra</th>
            <th scope="col">Entradas previstas</th>
            <th scope="col">Compromissos</th>
            {byPosition
              ? <>
                <th scope="col">Posição hoje</th>
                <th scope="col">{endLabel}</th>
              </>
              : <>
                <th scope="col">Cobertura</th>
                <th scope="col">Primeiro mês descoberto</th>
              </>}
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => <tr key={project.projectKey} data-uncovered={project.uncoveredMonths > 0 ? "true" : "false"}>
            <th scope="row">
              <Link to={`${BASE_PATH}/${encodeURIComponent(project.projectKey)}${companyQuery(selectedCompanyId)}`}>
                {project.name || "Obra sem nome"}
              </Link>
            </th>
            {row(project)}
          </tr>)}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total das obras</th>
            {row(summary)}
          </tr>
          {data.shared && <tr>
            <th scope="row">{data.shared.label ?? "Sem obra (compartilhado)"}</th>
            {row({ ...data.shared, coverageBps: null, firstUncoveredMonth: undefined })}
          </tr>}
          {data.total && <tr>
            <th scope="row">{ALL_LABEL}</th>
            {row(data.total.summary || {})}
          </tr>}
        </tfoot>
      </table>
    </div>
    <p className="director-owner-footnote">Clique numa obra para ver o mesmo gráfico só dela.</p>
  </article>;
};

const ReceivablesCommitments = () => {
  const { project_key: routeProjectKey } = useParams();
  const projectKey = routeProjectKey || null;
  const { selectedCompanyId, accessByCompany = {}, metadata = {} } = useDirectorContext();
  const [reading, setReading] = useState("flow");
  const [scopeAll, setScopeAll] = useState(false);
  const { clearProject } = useProjectRoute({
    routeKey: projectKey,
    pathFor: (key) => (key
      ? `${BASE_PATH}/${encodeURIComponent(key)}${companyQuery(selectedCompanyId)}`
      : `${BASE_PATH}${companyQuery(selectedCompanyId)}`),
  });
  const query = useMemo(() => ({
    from: currentCompetence(),
    months: 12,
    scenario: "base",
    projectKey,
  }), [projectKey]);
  const surface = useDirectorSurface("receivables-commitments", query);
  const { data } = surface;
  const capabilities = accessByCompany[selectedCompanyId]?.capabilities || [];
  const metadataProject = (metadata.projects || []).find((item) => item.key === projectKey);
  const project = data?.project || null;
  const projectName = project?.name || metadataProject?.name || "Obra";
  const wbsBuildingId = project?.wbsBuildingId || metadataProject?.wbsBuildingId || null;
  const canSeeAll = !projectKey && Boolean(data?.total);
  const showAll = canSeeAll && scopeAll;
  const view = showAll ? data.total : { summary: data?.summary, months: data?.months };
  const summary = view?.summary || {};
  const positionReady = hasPosition(summary);
  const activeReading = reading === "position" && positionReady ? "position" : "flow";
  const firstMonth = (showAll ? data?.position?.total : data?.position?.obras)?.firstMonth ?? null;
  const covered = Number.isSafeInteger(summary.coverageBps) && summary.coverageBps >= 10000;
  const freshness = oldestExtractedAt(data?.freshness);
  const months = data?.horizon?.months || 12;
  const flowTitle = projectKey ? "Entra x sai, mês a mês" : `Entra x sai, próximos ${months} meses`;

  return <ModuleShell
    eyebrow={projectKey ? "Diretoria · Recebíveis x compromissos" : undefined}
    title={projectKey ? projectName : "Recebíveis x compromissos"}
    description={projectKey
      ? `${project?.legalEntityName ? `${project.legalEntityName} · ` : ""}${data
        ? `as entradas previstas desta obra ${covered ? "cobrem" : "não cobrem"} o que ela já assumiu.`
        : "entradas previstas contra os compromissos desta obra."}`
      : "O que deve entrar (carteira e outras receitas) contra o que as obras já assumiram e ainda planejam gastar."}
    badge={`Próximos ${months} meses`}
    qualitySources={[surface]}
  >
    {projectKey && <nav className="director-owner-actions" aria-label="Navegação da obra">
      <Link className="director-link-button director-link-button--secondary" to={`${BASE_PATH}${companyQuery(selectedCompanyId)}`} onClick={clearProject}>
        Voltar à carteira
      </Link>
      {wbsBuildingId && <Link className="director-link-button" to={`/dashboard/${encodeURIComponent(wbsBuildingId)}`}>
        Abrir a obra no Ampli Obras
      </Link>}
      {capabilities.includes("director.portfolio.read") && <Link
        className="director-link-button director-link-button--secondary"
        to={`/dashboard/director/centers${companyQuery(selectedCompanyId)}`}
      >
        Ver centros e títulos da obra
      </Link>}
    </nav>}
    <ModuleState
      {...surface}
      availability={data?.status === "no_data" ? "empty" : undefined}
      onRetry={surface.reload}
    />
    {data && <ExcludedTitlesNotice summary={data.excludedTitles} companyId={selectedCompanyId} />}
    {data && data.status !== "no_data" && <>
      <div className="director-owner-switches">
        {positionReady && <Switch
          label="Leitura"
          value={activeReading}
          onChange={setReading}
          options={[
            { value: "flow", label: `Fluxo dos ${months} meses` },
            { value: "position", label: "Posição própria" },
          ]}
        />}
        {canSeeAll && <Switch
          label="Alcance"
          value={showAll ? "all" : "obras"}
          onChange={(value) => setScopeAll(value === "all")}
          options={[
            { value: "obras", label: "Só obras" },
            { value: "all", label: ALL_LABEL },
          ]}
        />}
      </div>
      {activeReading === "position" && <p className="director-owner-definition">
        <strong>Posição própria</strong> é tudo o que entrou menos tudo o que saiu nos centros de custo {projectKey ? "da obra" : "de cada obra"} desde
        o primeiro movimento, somado ao previsto mês a mês. Não é saldo bancário: transferências entre empresas, distribuição aos
        sócios e empréstimos que não viram título no Sienge ficam de fora. Posição positiva pode já ter saído da obra.
      </p>}
      <Kpis
        summary={summary}
        horizonMonths={data.horizon?.months}
        project={Boolean(projectKey)}
        reading={activeReading}
        firstMonth={firstMonth}
      />
      <article className="director-module1__panel">
        <header>
          <div>
            <span className="director-eyebrow">Mês a mês</span>
            <h2>{activeReading === "position" ? "Posição própria, mês a mês" : flowTitle}</h2>
          </div>
          <p>{activeReading === "position"
            ? "O número sobre cada mês é a posição no fim do mês: o que já acumulou mais o que entra menos o que sai."
            : "O número sobre cada mês é o que sobra ou falta naquele mês."}</p>
        </header>
        <CoverageBars months={view?.months || []} headline={activeReading === "position" ? "position" : "net"} />
        {!projectKey && <p className="director-owner-footnote">
          Obras em SPEs diferentes não compensam caixa entre si: o consolidado é uma soma para leitura, não uma transferência.
        </p>}
      </article>
      {!projectKey && <ProjectsTable data={data} selectedCompanyId={selectedCompanyId} reading={activeReading} />}
      {freshness && <footer className="director-freshness">Fontes atualizadas em {formatFreshness(freshness)}.</footer>}
    </>}
  </ModuleShell>;
};

export default ReceivablesCommitments;
